/*
  Sold-out size clicks open Klaviyo's back-in-stock form.

  Clicking the injected #klaviyo-bis-button-container button on a timer misses
  whenever Klaviyo is still rebuilding that button for the new variant. The
  collection grid already opens the form with openForm; this does the same
  once the variant id is on the product URL and the hidden variant input.
*/
if (!customElements.get("pdp-klaviyo-bis")) {
  let bisFormIdPromise = null;
  let openToken = 0;

  function klaviyoCompanyId() {
    const script = document.querySelector('script[src*="klaviyo.js"][src*="company_id"]');
    if (!script) return null;
    try {
      return new URL(script.src).searchParams.get("company_id");
    } catch (error) {
      return null;
    }
  }

  function getBisFormId() {
    if (bisFormIdPromise) return bisFormIdPromise;

    const companyId = klaviyoCompanyId();
    /* Klaviyo's script is often injected after this element connects. Don't cache a miss. */
    if (!companyId) return Promise.resolve(null);

    bisFormIdPromise = fetch(
      `https://static-forms.klaviyo.com/forms/api/v7/${encodeURIComponent(companyId)}/full-forms`
    )
      .then((response) => {
        if (!response.ok) {
          bisFormIdPromise = null;
          return null;
        }
        return response.json();
      })
      .then((data) => {
        if (!data) return null;
        const forms = (data && data.full_forms) || [];
        const match = forms.find((form) =>
          (form.live_form_versions || []).some((version) => {
            if ((version.form_specialties || []).indexOf("BACK_IN_STOCK") !== -1) return true;
            return (version.dynamic_buttons || []).some((button) => button.type === "BACK_IN_STOCK_OPEN");
          })
        );
        return match ? match.form_id : null;
      })
      .catch(() => {
        bisFormIdPromise = null;
        return null;
      });

    return bisFormIdPromise;
  }

  function selectedOptions(root) {
    return Array.from(root.querySelectorAll("select, fieldset"), (element) => {
      if (element.tagName === "SELECT") return element.value;
      return Array.from(element.querySelectorAll("input")).find((radio) => radio.checked)?.value;
    });
  }

  function findVariant(root) {
    const dataEl = root.querySelector('script[type="application/json"]');
    if (!dataEl) return null;

    let variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      return null;
    }

    const options = selectedOptions(root);
    return variants.find(
      (variant) => !variant.options.map((option, index) => options[index] === option).includes(false)
    );
  }

  function syncVariant(root, variant) {
    const id = String(variant.id);
    const sectionId = root.dataset.section;

    if (sectionId) {
      document
        .querySelectorAll(`#product-form-${sectionId}, #product-form-installment-${sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input || input.value === id) return;
          input.value = id;
        });
    }

    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
      window.ShopifyAnalytics.meta.selectedVariantId = id;
    }

    if (root.dataset.updateUrl === "false") return;

    const url = new URL(window.location.href);
    if (url.searchParams.get("variant") === id) return;
    url.searchParams.set("variant", id);
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }

  function bisDialog() {
    return document.querySelector('[role="dialog"][aria-label="POPUP Form"]');
  }

  function openForm(formId) {
    const klaviyo = window.klaviyo;
    if (klaviyo && typeof klaviyo.openForm === "function") {
      klaviyo.openForm(formId);
      return;
    }
    if (klaviyo && typeof klaviyo.push === "function") {
      klaviyo.push(["openForm", formId]);
      return;
    }
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(["openForm", formId]);
  }

  function clickHiddenButton() {
    const button = document.querySelector("#klaviyo-bis-button-container button");
    if (button) button.click();
  }

  function openBis(root, variant) {
    const token = ++openToken;
    syncVariant(root, variant);

    const run = () => {
      if (token !== openToken || bisDialog()) return;

      getBisFormId().then((formId) => {
        if (token !== openToken || bisDialog()) return;
        if (!formId) {
          clickHiddenButton();
          return;
        }
        openForm(formId);
      });
    };

    /* After this click finishes, so the opening click is not treated as a dismiss. */
    setTimeout(run, 0);
    /* Variant changes make Klaviyo rebuild its button and ignore the first open. */
    setTimeout(run, 600);
  }

  customElements.define(
    "pdp-klaviyo-bis",
    class PdpKlaviyoBis extends HTMLElement {
      connectedCallback() {
        this.root = this.closest("variant-selects");
        if (!this.root) return;
        if (this.onClick) this.root.removeEventListener("click", this.onClick);
        this.onClick = this.onClick || this.handleClick.bind(this);
        this.root.addEventListener("click", this.onClick);
        if (this.root.querySelector("[data-bis-trigger]")) getBisFormId();
      }

      disconnectedCallback() {
        if (this.root && this.onClick) this.root.removeEventListener("click", this.onClick);
      }

      triggerFrom(event) {
        const input = event.target.closest('input[type="radio"][data-bis-trigger]');
        if (input && this.root.contains(input)) return input;

        const label = event.target.closest("label[for]");
        if (!label || !this.root.contains(label)) return null;

        const control = document.getElementById(label.htmlFor);
        if (!control || !this.root.contains(control) || !control.hasAttribute("data-bis-trigger")) return null;
        return control;
      }

      handleClick(event) {
        const input = this.triggerFrom(event);
        if (!input || input.dataset.bisClick === "1") return;

        /* Label clicks and the forwarded input click are one tap. Wait a tick so the radio is checked. */
        input.dataset.bisClick = "1";
        setTimeout(() => {
          delete input.dataset.bisClick;
          if (!input.checked) return;
          const variant = findVariant(this.root);
          if (!variant) return;
          openBis(this.root, variant);
        }, 0);
      }
    }
  );
}
